import { useRef } from 'react';
import EfImg from '@elem/ui/ef-img';
import { FormatMoneyLocal } from '@elem/ui-common/utils/FormatMoneyLocal';
import { BaseCardBtnText } from 'lowcode/common/LangText/BaseCardBtnText';
import EfLink from '@elem/ui/ef-link';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper';
import getLocal from 'eco-design-reliance/getLocal';
import cookie from 'react-cookies';
import gtm from 'eco-design-reliance/gtm';
import { saveAICookieForShopify } from '@elem/ui-common/utils/CookieForShopify';

import './index.scss';

// 定义产品类型
type ProductItem = {
    productId: string;
    variantId: string;
    quantity: number;
    price: string;
    compareAtPrice: string;
    img: string;
    variantImg: string;
    productTitle: string;
    skuTitle: string;
    variantDescription: string;
    handle: string;
    specific: string;
    itemType?: string;
};

// 定义Bundle类型
type BundleItem = {
    itemId: string;
    itemType: string;
    retrieveId: string;
    score: number;
    variants: ProductItem[];
};

interface SingleProductProps {
    data: BundleItem[];
}

const formatOffText = (value: number, locale: string) => {
    const localeText = {
        us: `${value}% OFF`,
        de: `${value}% Rabatt`,
        fr: `${value}% OFF`,
        it: `Sconto ${value}%`,
        es: `Descuento${value}%`,
        jp: `${value}%OFF`,
        kr: `${value}% 할인`,
        br: `Descuento${value}%`,
        mx: `Descuento${value}%`,
        sa: `خصم  ${value}%`,
    };
    return localeText[locale as keyof typeof localeText] || localeText.us;
};

export default function SingleProduct({ data }: SingleProductProps) {
    const { locale = 'us' } = getLocal();
    const prevRef = useRef<HTMLDivElement>(null);
    const nextRef = useRef<HTMLDivElement>(null);

    const prefixCls = 'temp-member-product-ai-recommend';

    const TextA = BaseCardBtnText[locale] || BaseCardBtnText['us'];

    // 计算折扣百分比
    const getDiscountPercentage = (price: number, compareAtPrice: number) => {
        if (!compareAtPrice || compareAtPrice <= price) return null;
        const discount = Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
        const finalOffText = formatOffText(discount, locale);

        return discount > 0 ? finalOffText : null;
    };

    // 将 productRes2 的嵌套结构展平为单个产品列表
    const flattenProducts = (): ProductItem[] => {
        const allProducts: ProductItem[] = [];
        data?.forEach((item: BundleItem) => {
            if (item.variants && Array.isArray(item.variants)) {
                // 将每个 variant 作为独立产品添加到列表中
                item.variants.forEach((variant: ProductItem) => {
                    allProducts.push({
                        ...variant,
                        // 保留原有的 item 级别信息（如果需要）
                        itemType: item.itemType,
                    });
                });
            }
        });
        return allProducts;
    };

    const handleLinkClick = (item: any, index: number) => {
        sessionStorage.setItem('ai_recommend_module', 'AI Brand Member Recommend');

        // 新增 ai cookie 埋点
        const expires = new Date();
        expires.setDate(expires.getDate() + 7);

        const cookieOptions = {
            path: '/',
            domain: '.ecoflow.com',
            expires,
        };

        const handleCallback = () => {
            console.log('handleCallback');
            cookie.save('display_type', 'Shopify Product Page', {
                path: '/',
                domain: '.ecoflow.com',
                expires,
            });
            cookie.save('ai_entry_type', 'AI Brand Member Recommend_Shopify Product Page', { path: '/', domain: '.ecoflow.com' });
            cookie.save('ai_jump_url', `https://${locale}.ecoflow.com/products/${item.handle}?variant=${item.variantId}`, {
                path: '/',
                domain: '.ecoflow.com',
            });
            cookie.save('display_type', 'AI Brand Member Recommend_Shopify Product Page', cookieOptions);
        };

        saveAICookieForShopify(`https://${locale}.ecoflow.com/products/${item.handle}?variant=${item.variantId}`, locale, false, handleCallback);

        gtm.push({
            event: 'uaEvent',
            event_name: 'buy',
            product_name: item?.productTitle || item?.skuTitle || '',
            position: 'In-Product Signal Page',
            display_type: 'Shopify Product Page',
            module_referral: 'AI Brand Member Recommend',
            jump_module: `https://${locale}.ecoflow.com/products/${item.handle}?variant=${item.variantId}`,
        });
    };

    const handleCheckoutClick = (item: any, index: number) => {
        // 新增 ai cookie 埋点
        const expires = new Date();
        expires.setDate(expires.getDate() + 7);

        const cookieOptions = {
            path: '/',
            domain: '.ecoflow.com',
            expires,
        };

        cookie.save('ai_entry_type', 'AI Brand Member Recommend_Shopify Product Page', { path: '/', domain: '.ecoflow.com' });
        cookie.save('ai_jump_url', `https://${locale}.ecoflow.com/cart/${item?.variantId}:1?channel=buy_button`, { path: '/', domain: '.ecoflow.com' });
        cookie.save('display_type', 'AI Brand Member Recommend_Shopify Checkout Page', cookieOptions);

        gtm.push({
            event: 'uaEvent',
            event_name: 'checkout',
            button_name: `${TextA.BuyNow}_${item?.productTitle || item?.skuTitle || ''}`,
            display_type: 'Shopify Product Page',
            module_referral: 'AI Brand Member Recommend',
            jump_module: `https://${locale}.ecoflow.com/cart/${item?.variantId}:1?channel=buy_button`,
        });
    };

    const products = flattenProducts();

    return (
        <div className={`${prefixCls}-product-grid`}>
            {/* pc端 */}
            <div className={`${prefixCls}-product-grid-container`}>
                {products?.map((product: ProductItem, index: number) => {
                    const discountLabel = getDiscountPercentage(parseFloat(product?.price), parseFloat(product?.compareAtPrice));
                    return (
                        <div key={product?.variantId || index} className={`${prefixCls}-product-card`}>
                            {discountLabel && <div className={`${prefixCls}-product-discount-badge`}>{discountLabel}</div>}

                            <div className={`${prefixCls}-product-image`}>
                                <EfImg pc={product?.variantImg || product?.img} />
                            </div>

                            <div className={`${prefixCls}-product-info`}>
                                <h4 className={`${prefixCls}-product-title`}>{product?.productTitle || product?.skuTitle}</h4>

                                {product?.variantDescription && <p className={`${prefixCls}-product-subtitle`}>{product?.variantDescription}</p>}

                                <div className={`${prefixCls}-product-pricing`}>
                                    <div className={`${prefixCls}-product-pricing-member`}>
                                        <span className={`${prefixCls}-product-current-price`}>{FormatMoneyLocal(parseFloat(product?.price), locale)}</span>
                                        <div className={`${prefixCls}-product-member-tip-box`}>
                                            <div className={`${prefixCls}-product-member-tip-text`}>{TextA.MembersOnly}</div>
                                        </div>
                                    </div>

                                    {product?.compareAtPrice && product?.compareAtPrice !== product?.price && (
                                        <span className={`${prefixCls}-product-original-price`}>
                                            {FormatMoneyLocal(parseFloat(product?.compareAtPrice), locale)}
                                            {TextA.NonMemberPrice}
                                        </span>
                                    )}
                                </div>

                                <div className={`${prefixCls}-product-actions`}>
                                    <EfLink
                                        href={`https://${locale}.ecoflow.com/products/${product?.handle}?variant=${product.variantId}`}
                                        className={`${prefixCls}-product-btn ${prefixCls}-product-btn-learn`}
                                        onClick={() => handleLinkClick(product, index)}
                                    >
                                        {TextA.LearnMore}
                                    </EfLink>

                                    <EfLink
                                        href={`https://${locale}.ecoflow.com/cart/${product?.variantId}:1?channel=buy_button`}
                                        className={`${prefixCls}-product-btn ${prefixCls}-product-btn-buy`}
                                        onClick={() => handleCheckoutClick(product, index)}
                                    >
                                        {TextA.BuyNow}
                                    </EfLink>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* 移动端 */}
            <div className={`${prefixCls}-product-swiper`}>
                <Swiper
                    modules={[Navigation]}
                    spaceBetween={16}
                    slidesPerView={'auto'}
                    navigation={{
                        prevEl: `.${prefixCls}-swiper-btn-prev`,
                        nextEl: `.${prefixCls}-swiper-btn-next`,
                    }}
                    className={`${prefixCls}-swiper`}
                >
                    {products?.map((product: ProductItem, index: number) => {
                        const discountLabel = getDiscountPercentage(parseFloat(product?.price), parseFloat(product?.compareAtPrice));
                        return (
                            <SwiperSlide key={product?.variantId || index} className={`${prefixCls}-swiper-slide`}>
                                <div className={`${prefixCls}-product-card`}>
                                    {discountLabel && <div className={`${prefixCls}-product-discount-badge`}>{discountLabel}</div>}

                                    <div className={`${prefixCls}-product-image`}>
                                        <EfImg pc={product?.variantImg || product?.img} />
                                    </div>

                                    <div className={`${prefixCls}-product-info`}>
                                        <h4 className={`${prefixCls}-product-title`}>{product?.productTitle || product?.skuTitle}</h4>

                                        {product?.variantDescription && <p className={`${prefixCls}-product-subtitle`}>{product?.variantDescription}</p>}

                                        <div className={`${prefixCls}-product-pricing`}>
                                            <div className={`${prefixCls}-product-pricing-member`}>
                                                <span className={`${prefixCls}-product-current-price`}>
                                                    {FormatMoneyLocal(parseFloat(product?.price), locale)}
                                                </span>
                                                <div className={`${prefixCls}-product-member-tip-box`}>
                                                    <div className={`${prefixCls}-product-member-tip-text`}>{TextA.MembersOnly}</div>
                                                </div>
                                            </div>

                                            {product?.compareAtPrice && product?.compareAtPrice !== product?.price && (
                                                <span className={`${prefixCls}-product-original-price`}>
                                                    {FormatMoneyLocal(parseFloat(product?.compareAtPrice), locale)}
                                                    {TextA.NonMemberPrice}
                                                </span>
                                            )}
                                        </div>

                                        <div className={`${prefixCls}-product-actions`}>
                                            <EfLink
                                                href={`https://${locale}.ecoflow.com/products/${product?.handle}?variant=${product.variantId}`}
                                                className={`${prefixCls}-product-btn ${prefixCls}-product-btn-learn`}
                                                onClick={() => handleLinkClick(product, index)}
                                            >
                                                {TextA.LearnMore}
                                            </EfLink>

                                            <EfLink
                                                href={`https://${locale}.ecoflow.com/cart/${product?.variantId}:1?channel=buy_button`}
                                                className={`${prefixCls}-product-btn ${prefixCls}-product-btn-buy`}
                                                onClick={() => handleCheckoutClick(product, index)}
                                            >
                                                {TextA.BuyNow}
                                            </EfLink>
                                        </div>
                                    </div>
                                </div>
                            </SwiperSlide>
                        );
                    })}
                </Swiper>

                {/* 移动端导航按钮 */}
                <div className={`${prefixCls}-swiper-navigation`}>
                    <div ref={prevRef} className={`${prefixCls}-swiper-btn ${prefixCls}-swiper-btn-prev`}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M12.5 5L7.5 10L12.5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <div ref={nextRef} className={`${prefixCls}-swiper-btn ${prefixCls}-swiper-btn-next`}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                </div>
            </div>
        </div>
    );
}
